import { GenericError } from "@via/components/error.tsx";
import Modal from "@via/components/modal.tsx";
import { HorizontalTabs } from "@via/components/tabs.tsx";
import PremiumDatasetTable from "@via/pages/data/premium-dataset/components/premium-dataset-table.tsx";
import { usePremiumDatasetList } from "@via/pages/data/premium-dataset/premium-dataset.api.tsx";
import { ActionButton } from "@via/components/action-button/action-button";
import {
  BUTTONS,
  ERROR,
  INPUT_FILE_MODAL_TABS,
  ModalSize,
  ROUTES,
} from "@via/utils/constants.tsx";
import { type CollectionItem } from "@via/utils/interfaces.tsx";
import {
  AwsFileUploadPath,
  DatasetListResponseItem,
  ExecutorJobsType,
  GcsFileUploadPath,
  InputType,
  type SaveRunInputs,
  SshFileUploadPath,
  type VmetaDataSetResponse,
} from "common";
import { type FC, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { UseFormReturn, useWatch } from "react-hook-form";
import FileUploader from "@via/components/file-uploader/file-uploader";
import { fileUpload } from "@via/pages/data/data-sources/cloud-data-sources/common/file-upload.ts";
import { useNotifications } from "@via/hooks/useNotifications";
import { prepareBucketPath } from "@via/pages/data/data-sources/data-source.api";
import {
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner";
import { useAuth } from "@via/hooks/useAuth";
import { PremiumDatasetViewType } from "@via/pages/data/data-collections/components/data-collection-filter";
import {
  getRunUploadDir,
  runInputPathAfterUpload,
} from "@via/pages/projects/runs/utils/helpers.tsx";
import { uploadTargetForRun } from "@via/pages/data/data-sources/ssh-data-source/agent-listing-notices.ts";

interface InputFileModalProps {
  fileOrCollection: Record<string, unknown>;
  setFileOrCollection: (
    fileOrCollection: Record<string, unknown> | null
  ) => void;
  onSaveFiles: (name: string, collection: CollectionItem) => void;
  onSaveValue: (inputId: string, value: string | null) => void;
  paramsInputs: SaveRunInputs[];
  form: UseFormReturn;
}

const InputFileModal: FC<InputFileModalProps> = ({
  fileOrCollection,
  setFileOrCollection,
  onSaveFiles,
  onSaveValue,
  paramsInputs,
  form,
}) => {
  const { runId = "" } = useParams();
  const { setNotification } = useNotifications();
  const { user } = useAuth();
  const defaultInputType = paramsInputs.find(
    ({ name }) =>
      name === Object.keys(fileOrCollection)?.[0]?.replace("params.", "")
  )?.type;
  const initialManualFilePath =
    typeof Object.values(fileOrCollection)[0] === "string" &&
    defaultInputType === InputType.Input
      ? (Object.values(fileOrCollection)[0] as string)
      : "";
  const [manualFilePath, setManualFilePath] = useState(initialManualFilePath);
  const [isLoading, setIsLoading] = useState(false);
  const [progressPercentageList, setProgressPercentageList] = useState<
    { filename: string; progress: number }[]
  >([]);
  const [selectedDataset, setSelectedDataset] = useState<
    DatasetListResponseItem | VmetaDataSetResponse | null
  >(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sort, setSort] = useState<Array<{ id: string; desc: boolean }>>([
    { id: "updatedAt", desc: true },
  ]);
  // Get the current value to determine if a dataset is already selected
  const currentValue = Object.values(fileOrCollection)?.[0];
  const currentCollectionName =
    typeof currentValue === "string" && defaultInputType !== InputType.Input
      ? currentValue
      : null;
  const [premiumColumnFilters, setPremiumColumnFilters] = useState<
    Array<{ id: string; value: unknown }>
  >(() => {
    // Initialize with current collection name if it exists and it's a premium dataset
    if (
      currentCollectionName &&
      fileOrCollection &&
      defaultInputType === InputType.VmetaCollection
    ) {
      return [{ id: "name", value: currentCollectionName }];
    }
    return [];
  });
  const [activeTab, setActiveTab] = useState<string>("");
  const [premiumDatasetViewType, setPremiumDatasetViewType] =
    useState<PremiumDatasetViewType>(PremiumDatasetViewType.My);

  // File upload logic (similar to InputSingleFileModal)
  const selectedRunEnvironment = form.watch("selectedRunEnvironment");

  const watchedSomeSpecificFormValues = useWatch({
    name: ["launchDirectory", "publishDir", "workDirectory"],
    control: form.control,
  });

  const [launchDirectory, publishDir, workDirectory] =
    watchedSomeSpecificFormValues;

  const isCloudJob =
    selectedRunEnvironment?.executorJob === ExecutorJobsType.GoogleBatch ||
    selectedRunEnvironment?.executorJob === ExecutorJobsType.AwsBatch;

  const isAgentEnvironment = selectedRunEnvironment?.transport === "agent";

  const runUploadDir = useMemo(() => {
    return getRunUploadDir({
      isCloudJob,
      workDirectory,
      publishDir,
      launchDirectory,
      runId,
    });
  }, [isCloudJob, workDirectory, publishDir, launchDirectory, runId]);

  const isDisabledUpload = useMemo(() => {
    return !selectedRunEnvironment || !runUploadDir;
  }, [selectedRunEnvironment, runUploadDir]);

  const credential = useMemo(() => {
    if (selectedRunEnvironment?.executorJob === ExecutorJobsType.AwsBatch) {
      return {
        id: selectedRunEnvironment.amazonCreId,
        path: AwsFileUploadPath,
        type: ExecutorJobsType.AwsBatch,
      };
    } else if (
      selectedRunEnvironment?.executorJob === ExecutorJobsType.GoogleBatch
    ) {
      return {
        id: selectedRunEnvironment.googleCreId,
        path: GcsFileUploadPath,
        type: ExecutorJobsType.GoogleBatch,
      };
    } else {
      return {
        id: selectedRunEnvironment?.id,
        path: SshFileUploadPath,
        type: ExecutorJobsType.Local,
      };
    }
  }, [selectedRunEnvironment]);

  const uploadCredentials = useMemo(() => {
    const credentials = {
      prefix: "",
      savedCredentialId: credential.id,
      bucketName: runUploadDir,
    };
    prepareBucketPath(credentials);
    if (credential.type === ExecutorJobsType.Local) {
      credentials.prefix = "/" + credentials.prefix;
    }
    return credentials;
  }, [runUploadDir, credential]);

  const handleSetPremiumColumnFilters = (
    filters: (
      prevFilters: Array<{ id: string; value: unknown }>
    ) => Array<{ id: string; value: unknown }>
  ) => {
    setPremiumColumnFilters(filters);
  };

  const {
    data: vmetaDatasets,
    isLoading: isVmetaDatasetsLoading,
    isError: isVmetaDatasetsError,
  } = usePremiumDatasetList(
    page,
    pageSize,
    sort,
    premiumColumnFilters,
    activeTab === INPUT_FILE_MODAL_TABS.DATASET,
    premiumDatasetViewType,
    user?.vmeta?.userId
  );

  useEffect(() => {
    if (!activeTab) {
      if (defaultInputType === InputType.Input) {
        setActiveTab(INPUT_FILE_MODAL_TABS.MANUAL);
      } else {
        setActiveTab(INPUT_FILE_MODAL_TABS.DATASET);
      }
    }
  }, [defaultInputType, activeTab, paramsInputs, fileOrCollection]);

  const handleDatasetSelect = (
    dataset: DatasetListResponseItem | VmetaDataSetResponse
  ): void => {
    setSelectedDataset(dataset);
  };

  const handleManualFileUpdate = (value: string): void => {
    setManualFilePath(value.trim());
  };

  const handleFileUpload = (files: File[]): void => {
    const uploadedFile = files[0];
    const uploadedFileName = uploadedFile.name.split("/").slice(-1).join("");

    fileUpload(
      credential.path,
      files,
      uploadCredentials,
      setNotification,
      (placedPath?: string) =>
        handleFileUploadSuccess(uploadedFileName, placedPath),
      setIsLoading,
      setProgressPercentageList,
      // agent-5-5: only an agent environment takes a target; SSH keeps today's behavior.
      isAgentEnvironment ? uploadTargetForRun(runId) : undefined
    );
  };

  const handleFileUploadSuccess = (
    uploadedFileName: string,
    placedPath?: string
  ) => {
    setProgressPercentageList([]);
    setManualFilePath(
      runInputPathAfterUpload(runUploadDir, uploadedFileName, placedPath)
    );
  };

  const getUploadValidationMessages = (): string[] => {
    if (!selectedRunEnvironment) {
      return ["You should select run environment!"];
    }

    if (!runUploadDir) {
      return [
        isCloudJob
          ? "For AWS/Google Batch environments, you must enable Publish Directory or provide a valid Work Directory path!"
          : "You must provide a valid Execution Directory or Work Directory path!",
      ];
    }

    return [];
  };

  // Set the selected dataset when data loads if there's a current value
  useEffect(() => {
    if (selectedDataset || !currentCollectionName) return;

    const match = vmetaDatasets?.data?.find(
      ({ name }) => name === currentCollectionName
    );

    if (match && match !== selectedDataset) handleDatasetSelect(match);
  }, [
    vmetaDatasets?.data,
    currentCollectionName,
    selectedDataset,
    handleDatasetSelect,
  ]);

  const handleSaveDataset = async (): Promise<void> => {
    const gNum = Object.keys(fileOrCollection)?.[0];

    // Save based on the currently active tab, not on which field happens to
    // have a value.
    if (activeTab === INPUT_FILE_MODAL_TABS.MANUAL) {
      if (manualFilePath.length > 0) onSaveValue(gNum, manualFilePath);
      return;
    }

    // Dataset tab (default for non-Input params)
    if (selectedDataset) {
      const collection: CollectionItem = {
        collectionId:
          "_id" in selectedDataset
            ? (selectedDataset as VmetaDataSetResponse)._id.toString()
            : (selectedDataset as DatasetListResponseItem).id.toString(),
        collectionName: selectedDataset.name,
        collectionType:
          "_id" in selectedDataset
            ? InputType.VmetaCollection
            : InputType.Collection,
      };
      onSaveFiles(gNum, collection);
    }
  };

  return (
    <>
      <Modal
        open={!!fileOrCollection}
        setOpen={() => {
          setFileOrCollection(null);
          // Reset state when modal closes
          setSelectedDataset(null);
          setManualFilePath("");
          setPremiumColumnFilters(() => []);
          setIsLoading(false);
          setProgressPercentageList([]);
        }}
        size={ModalSize.Large}
        title="Select Dataset"
        confirmText="Save"
        isConfirmLoading={false}
        isConfirmDisabled={
          activeTab === INPUT_FILE_MODAL_TABS.MANUAL
            ? !manualFilePath.length
            : !selectedDataset
        }
        onConfirm={handleSaveDataset}>
        <div className="flex flex-col pt-2 md:pt-3 lg:pt-4">
          {isVmetaDatasetsError ? (
            <div className="via-generic-error w-full justify-center">
              <GenericError message={ERROR} />
            </div>
          ) : (
            <>
              <HorizontalTabs
                buttonIdPrefix="input-file"
                activeTab={activeTab}
                onTabChange={setActiveTab}
                options={[
                  {
                    name: INPUT_FILE_MODAL_TABS.DATASET,
                    children: (
                      <div className="flex min-h-[200px] w-[1008px] items-start py-8">
                        <div className="flex w-full flex-col">
                          <div className="flex w-full justify-end">
                            <Link to={ROUTES.VMETA_DATASETS} target="_blank">
                              <ActionButton
                                button={BUTTONS.MANAGE_DATASET}
                                primary={true}
                              />
                            </Link>
                          </div>
                          <PremiumDatasetTable
                            data={vmetaDatasets?.data}
                            isLoading={isVmetaDatasetsLoading}
                            page={page}
                            setPage={setPage}
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                            sort={sort}
                            setSort={setSort}
                            columnFilters={premiumColumnFilters}
                            setColumnFilters={handleSetPremiumColumnFilters}
                            premiumDatasetViewType={premiumDatasetViewType}
                            onPremiumDatasetViewTypeChange={
                              setPremiumDatasetViewType
                            }
                            total={vmetaDatasets?.results}
                            mode="selection"
                            selectedDataset={
                              selectedDataset as VmetaDataSetResponse
                            }
                            onDatasetSelect={handleDatasetSelect}
                          />
                        </div>
                      </div>
                    ),
                  },
                  {
                    name: INPUT_FILE_MODAL_TABS.MANUAL,
                    children: (
                      <div className="flex min-h-[200px] w-[1008px] flex-col items-start py-8">
                        {isDisabledUpload && (
                          <div className="-mt-8 mb-4 w-full">
                            <MessageBanner
                              messages={getUploadValidationMessages()}
                              type={BannerType.Warning}
                            />
                          </div>
                        )}
                        <div className="flex w-full flex-col gap-6">
                          <div className="flex w-full flex-col">
                            <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
                              File Path
                            </label>
                            <input
                              value={manualFilePath}
                              onChange={e =>
                                handleManualFileUpdate(e.target.value)
                              }
                              placeholder="You can enter the full path of a file, Amazon (S3) or Google (GS) location"
                              type="text"
                              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                            <p className="mt-2 text-xs italic text-gray-500">
                              Enter the path to the file you would like to use
                              on disk or public data from the internet
                            </p>
                          </div>
                          <div className="flex w-full flex-col">
                            <FileUploader
                              onUpload={handleFileUpload}
                              maxFiles={1}
                              isLoading={isLoading}
                              progressPercentageList={progressPercentageList}
                              prefix={uploadCredentials.prefix}
                              isDisabled={isDisabledUpload}
                              containerClassName="w-250"
                            />
                          </div>
                        </div>
                      </div>
                    ),
                  },
                ]}
              />
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

export default InputFileModal;
